import type { CSSProperties } from 'react'
import type { DataChartStyleProps } from './datachartStyleRegistry'
import { useEnergyDeviceSnapshot } from './useEnergyDeviceSnapshot'
import './EnergyCard.css'

type PhaseCard = {
  id: 'r' | 's' | 't'
  label: string
  color: string
  power: number | null
  voltage: number | null
  current: number | null
  gaugePercent: number
}

const GAUGE_PATH = 'M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831'

function formatInt(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '--'
  return value.toLocaleString('en-US')
}

function formatFixed(value: number | null, digits: number): string {
  if (value === null || !Number.isFinite(value)) return '--'
  return value.toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })
}

function GaugePhaseCard({ phase }: { phase: PhaseCard }) {
  return (
    <div className="energy-card-stitch__phase">
      <div className="energy-card-stitch__gauge-wrap">
        <svg className="energy-card-stitch__gauge-svg" viewBox="0 0 36 36" aria-hidden>
          <path className="energy-card-stitch__gauge-bg" d={GAUGE_PATH} strokeDasharray="75, 100" />
          <path
            className="energy-card-stitch__gauge-value"
            d={GAUGE_PATH}
            strokeDasharray={`${phase.gaugePercent}, 100`}
            style={{ ['--phase-color' as string]: phase.color } as CSSProperties}
          />
        </svg>
        <span
          className="energy-card-stitch__phase-badge"
          style={{ ['--phase-color' as string]: phase.color } as CSSProperties}
        >
          {phase.label}
        </span>
      </div>

      <div className="energy-card-stitch__phase-power">
        {formatInt(phase.power)}
        <small>W</small>
      </div>
      <div className="energy-card-stitch__phase-meta">
        <span>{formatFixed(phase.voltage, 1)}V</span>
        <span className="energy-card-stitch__phase-divider" aria-hidden>
          |
        </span>
        <span>{formatFixed(phase.current, 1)}A</span>
      </div>
    </div>
  )
}

export function EnergyCardStitchBase({
  phaseCards,
  totalPower,
  frequency,
  powerFactor,
  showSummary = true,
  rootClassName = 'energy-card--stitch',
  showHeader = false,
  showHeaderMeta = false,
}: {
  phaseCards: PhaseCard[]
  totalPower: number | null
  frequency: number | null
  powerFactor: number | null
  showSummary?: boolean
  rootClassName?: string
  showHeader?: boolean
  showHeaderMeta?: boolean
}) {
  return (
    <div className={`energy-card ${rootClassName}`} data-css-slot="cardFrame" data-css-slot-label="Card Frame">
      {showHeader ? (
        <div className="energy-card-stitch__header">
          <div className="energy-card-stitch__title">三相電錶即時監測</div>
          {showHeaderMeta ? (
            <div className="energy-card-stitch__actions">
              <div className="energy-card-stitch__site">SITE A-01</div>
              <button type="button" className="energy-card-stitch__settings" aria-label="Open settings">
                <span className="material-symbols-rounded">settings</span>
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="energy-card-stitch__panel">
        <div className="energy-card-stitch__live">
          <span>LIVE</span>
          <span className="energy-card-stitch__live-dot" />
        </div>

        <div className="energy-card-stitch__combined-label">Combined Load</div>
        <div className="energy-card-stitch__combined-value">
          {formatInt(totalPower)}
          <small>W</small>
        </div>

        <div className="energy-card-stitch__phase-grid">
          {phaseCards.map((phase) => (
            <GaugePhaseCard key={phase.id} phase={phase} />
          ))}
        </div>
      </div>

      {showSummary ? (
        <div className="energy-card-stitch__summary-grid">
          <div className="energy-card-stitch__summary-card">
            <span>Frequency</span>
            <strong>
              {formatFixed(frequency, 2)} <small>Hz</small>
            </strong>
          </div>
          <div className="energy-card-stitch__summary-card">
            <span>Power Factor</span>
            <strong>
              {formatFixed(powerFactor, 2)} <small>phi</small>
            </strong>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export function EnergyCardStyle02(props: DataChartStyleProps) {
  const { snapshot } = useEnergyDeviceSnapshot(props.deviceSN, props.credentials)
  const phaseCards: PhaseCard[] = [
    {
      id: 'r',
      label: 'R',
      color: '#ff3b3b',
      power: snapshot?.phases.r.power ?? null,
      voltage: snapshot?.phases.r.voltage ?? null,
      current: snapshot?.phases.r.current ?? null,
      gaugePercent: 55,
    },
    {
      id: 's',
      label: 'S',
      color: '#ffcc00',
      power: snapshot?.phases.s.power ?? null,
      voltage: snapshot?.phases.s.voltage ?? null,
      current: snapshot?.phases.s.current ?? null,
      gaugePercent: 35,
    },
    {
      id: 't',
      label: 'T',
      color: '#007aff',
      power: snapshot?.phases.t.power ?? null,
      voltage: snapshot?.phases.t.voltage ?? null,
      current: snapshot?.phases.t.current ?? null,
      gaugePercent: 45,
    },
  ]
  return (
    <EnergyCardStitchBase
      phaseCards={phaseCards}
      totalPower={snapshot?.totalPower ?? null}
      frequency={snapshot?.frequency ?? null}
      powerFactor={snapshot?.powerFactor ?? null}
      showSummary={false}
      rootClassName="energy-card--stitch energy-card--stitch-no-summary"
    />
  )
}
